<!--
  Find file: a selection that names a file of the repository (see
  logic/findFile) gets a small offer above it, "Find file 'name'", told to
  screen readers politely. Taking it (or `gF`) opens a picker of
  the repository's files at the pull request's head that the name may mean,
  files of the pull request first; choosing one opens it in the Files view
  (at its lines, for `path:42` or `#L42`), as a diff when the pull request
  changes it and read-only otherwise. The offer never takes the focus or the
  selection, so copying works as it always does.

  The file list is RepoTree's: one recursive git trees call per head commit,
  shared with the explorer's "All files" and cached; the first selection
  that looks like a path loads it, so the offer shows only for names the
  repository has. When GitHub truncates it, a selection makes no further
  request: the offer shows for any path, and only once it is taken does the
  picker load the folders the query names (one call each). Only folders
  loaded so far are searched, and the picker says so and links to GitHub's
  code search.

  A file opens in the comparison the Files view shows (`from`, `to`, `ws`).
-->
<script lang="ts">
  import { onDestroy, onMount, tick } from 'svelte';
  import type { RepoTree } from '../state/repotree.svelte';
  import { findFile, selectedRef } from '../state/findFile.svelte';
  import { codeSearchUrl, findHref, offerable, queryDirs, rankMatches, type FileMatch } from '../logic/findFile';
  import { carryFilter } from '../logic/filefilter';
  import { nav } from '../nav.svelte';
  import LoadError from './LoadError.svelte';
  import { keyboard } from '../keys/keyboard.svelte';
  import { controlCommand } from '../keys/control';
  import { findFileListKeys } from '../keys/controls';

  interface Props {
    owner: string;
    repo: string;
    /** The Files view a match opens (a pull request's, a compare's). */
    filesPath: string;
    /** The repository's files at the head; null until the diff model has one. */
    tree: RepoTree | null;
    /** Paths the pull request changes (present at the head). */
    changed: Set<string>;
    webUrl: string;
    /** Where selections are read: the PR page's content. */
    root: HTMLElement | null;
  }
  let { owner, repo, filesPath, tree, changed, webUrl, root }: Props = $props();

  $effect(() => {
    findFile.root = root;
  });
  onDestroy(() => {
    findFile.root = null;
    findFile.offer = null;
    findFile.close();
  });

  /**
   * For the picker: the tree loaded, and in a partly listed tree the
   * folders `query` names. The offer only loads the tree, so a selection
   * alone never loads folders.
   */
  async function prepare(t: RepoTree, query: string) {
    await t.load();
    if (t.status !== 'ready' || !t.lazy) return;
    for (const d of queryDirs(query)) await t.expand(d);
  }

  /** Every path a query is matched against: the tree's and the pull request's. */
  function candidates(t: RepoTree): Set<string> {
    const paths = new Set(t.paths);
    for (const p of changed) paths.add(p);
    return paths;
  }

  // ------------------------------------------------------------ the offer
  /** A pointer is down: a selection being dragged is not offered until it ends. */
  let pressing = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** Bumped by each check, so a check that waited on the tree yields to a newer one. */
  let checks = 0;
  async function check() {
    if (timer) clearTimeout(timer);
    timer = null;
    if (pressing || findFile.picking) return;
    const at = ++checks;
    const hit = selectedRef(root);
    const t = tree;
    if (!hit || !t) {
      findFile.offer = null;
      return;
    }
    await t.load();
    // A newer check, or a drag begun while the tree loaded, decides instead.
    if (at !== checks || pressing || t.status !== 'ready') return;
    const now = selectedRef(root);
    if (!now || now.ref.query !== hit.ref.query) return;
    findFile.offer = offerable(now.ref, rankMatches(now.ref.query, candidates(t), changed), t.lazy) ? now : null;
  }
  function soon() {
    if (timer) clearTimeout(timer);
    // A collapsed selection hides the offer at once; a new one waits for the selection to settle.
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) findFile.offer = null;
    timer = setTimeout(check, 250);
  }
  function onpointerdown(e: PointerEvent) {
    if ((e.target as Element | null)?.closest?.('.find-file-offer')) return;
    pressing = true;
  }
  function onpointerup() {
    if (!pressing) return;
    pressing = false;
    // After the click's own selection change (a click collapses the selection).
    setTimeout(check, 0);
  }
  /** A drag that ends outside the window, or a cancelled pointer, sends no pointerup. */
  function release() {
    pressing = false;
  }
  function hide() {
    findFile.offer = null;
  }
  const FIND = { kind: 'goto', place: 'named-file' } as const;
  // While the offer shows it is a popover to the key router: cancelling dismisses it, and only it.
  const offered = $derived(!!findFile.offer);
  $effect(() => {
    if (!offered) return;
    return keyboard.use({ scope: 'offer', handle: (c) => (c.kind === 'cancel' ? hide() : 'pass') });
  });
  onMount(() => {
    document.addEventListener('selectionchange', soon);
    document.addEventListener('pointerdown', onpointerdown, true);
    document.addEventListener('pointerup', onpointerup, true);
    document.addEventListener('pointercancel', release, true);
    window.addEventListener('blur', release);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('selectionchange', soon);
      document.removeEventListener('pointerdown', onpointerdown, true);
      document.removeEventListener('pointerup', onpointerup, true);
      document.removeEventListener('pointercancel', release, true);
      window.removeEventListener('blur', release);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  });

  const OFFER_W = 280;
  const OFFER_H = 30;
  const offerStyle = $derived.by(() => {
    const o = findFile.offer;
    if (!o) return '';
    const left = Math.max(8, Math.min(o.at.left, window.innerWidth - OFFER_W - 8));
    // Above the selection, clear of the lines a reader goes on to; below it at the top of the window.
    const above = o.at.top - OFFER_H - 4;
    const maxTop = window.innerHeight - OFFER_H - 8;
    const top = Math.max(8, Math.min(maxTop, above >= 8 ? above : o.at.bottom + 4));
    return `left:${left}px;top:${top}px`;
  });

  // ------------------------------------------------------------ the picker
  let dialog: HTMLDialogElement | undefined = $state();
  let list: HTMLUListElement | undefined = $state();
  const ref = $derived(findFile.picking);

  $effect(() => {
    if (!dialog) return;
    if (ref && !dialog.open) dialog.showModal();
    else if (!ref && dialog.open) dialog.close();
  });
  $effect(() => {
    if (ref && tree) void prepare(tree, ref.query);
  });

  const loading = $derived(!tree || tree.status === 'idle' || tree.status === 'loading');
  const matches = $derived.by((): FileMatch[] => {
    if (!ref || !tree || tree.status !== 'ready') return [];
    return rankMatches(ref.query, candidates(tree), changed);
  });
  /** The Files view shows a comparison other than base → head, which a found file opens in. */
  const otherComparison = $derived(!!(nav.query.get('from') || nav.query.get('to')));
  const searchHref = $derived(ref ? codeSearchUrl(webUrl, owner, repo, ref.query) : '');
  const lineText = $derived(!ref?.line ? '' : ref.line.start === ref.line.end ? `line ${ref.line.start}` : `lines ${ref.line.start}–${ref.line.end}`);

  // The first match takes the focus once the list shows.
  let focusedFor: unknown = null;
  $effect(() => {
    if (!ref || !matches.length || focusedFor === matches) return;
    focusedFor = matches;
    void tick().then(() => list?.querySelector<HTMLButtonElement>('button')?.focus());
  });

  function choose(m: FileMatch) {
    const r = ref;
    findFile.close();
    if (!r) return;
    nav.go(carryFilter(findHref(filesPath, m.path, r.line, nav.query), nav.query));
  }

  /** The match list's keys (see `findFileListKeys`). */
  function onlistkey(e: KeyboardEvent) {
    const c = controlCommand(findFileListKeys, e);
    if (!c) return;
    if (c === 'close') {
      e.preventDefault();
      findFile.close();
      return;
    }
    const buttons = [...(list?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
    if (!buttons.length) return;
    e.preventDefault();
    const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (c === 'open') return void (i >= 0 ? buttons[i] : buttons[0]).click();
    const next = c === 'first' ? 0 : c === 'last' ? buttons.length - 1 : Math.max(0, Math.min(buttons.length - 1, i + (c === 'next' ? 1 : -1)));
    buttons[next].focus();
  }

  const dirOf = (p: string) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/') + 1) : '');
  const nameOf = (p: string) => p.slice(p.lastIndexOf('/') + 1);
</script>

{#if findFile.offer}
  {@const o = findFile.offer}
  <button
    class="find-file-offer"
    style={offerStyle}
    title="Find this file in the repository{keyboard.hint(FIND)}"
    onmousedown={(e) => e.preventDefault()}
    onclick={() => findFile.pick(o.ref)}
  >
    Find file <span class="q">‘{o.ref.query}’</span>{#if keyboard.key(FIND)}<kbd>{keyboard.key(FIND)}</kbd>{/if}
  </button>
{/if}
<div class="sr-only" role="status" aria-live="polite">
  {findFile.offer ? `Find file available for ${findFile.offer.ref.query}${keyboard.key(FIND) ? `: press ${[...keyboard.key(FIND)!].map((k) => (k !== k.toLowerCase() ? `Shift ${k}` : k)).join(', then ')}` : ''}.` : ''}
</div>

<dialog
  class="sheet find-file"
  bind:this={dialog}
  onclose={() => findFile.close()}
  onclick={(e) => {
    if (e.target === dialog) findFile.close();
  }}
  aria-labelledby="find-file-title"
>
  {#if ref}
    <div class="sheet-head">
      <h2 id="find-file-title">Find file <code>{ref.query}</code></h2>
      <button class="btn small" onclick={() => findFile.close()}>Close · Esc</button>
    </div>
    <p>
      Files at the head of this pull request{#if lineText}, opened at {lineText}{/if}. Files this pull request changes open as
      their diff; others open read-only.{#if otherComparison}
        Each opens in the comparison the Files view shows now.{/if}
    </p>
    {#if tree?.status === 'error' && tree.failure}
      <LoadError failure={tree.failure} what="the repository's file list" onretry={() => void tree?.load()} />
      <p><a href={searchHref} target="_blank" rel="noreferrer">Search for it on GitHub ↗</a></p>
    {:else if loading}
      <p role="status">Loading the repository's file list…</p>
    {:else}
      {#if tree?.lazy}
        <p class="note" role="note">
          This repository is too large for GitHub to list in one request, so only the folders opened so far are searched.
          <a href={searchHref} target="_blank" rel="noreferrer">Search for it on GitHub ↗</a>
        </p>
      {/if}
      {#if matches.length}
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions (arrow keys move between the buttons it holds) -->
        <ul class="matches" bind:this={list} onkeydown={onlistkey} aria-label="Matching files">
          {#each matches as m (m.path)}
            <li>
              <button onclick={() => choose(m)}>
                <span class="path"><span class="dir">{dirOf(m.path)}</span><strong>{nameOf(m.path)}</strong></span>
                {#if m.changed}<span class="in-pr">in this PR</span>{:else}<span class="read-only">read-only</span>{/if}
              </button>
            </li>
          {/each}
        </ul>
        <p class="count">{matches.length === 1 ? 'One file matches' : `${matches.length} files match`}{matches[0].kind === 'fuzzy' ? ' loosely' : ''}. ↑ ↓ to move, Enter to open, Esc to close.</p>
      {:else}
        <p role="status">
          No file at the head matches <code>{ref.query}</code>.
          {#if !tree?.lazy}<a href={searchHref} target="_blank" rel="noreferrer">Search for it on GitHub ↗</a>{/if}
        </p>
      {/if}
    {/if}
  {/if}
</dialog>

<style>
  .find-file-offer {
    position: fixed;
    z-index: 60;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 280px;
    padding: 4px 6px 4px 10px;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: var(--surface);
    color: var(--fg);
    box-shadow: 0 6px 20px #0002;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
  }
  .find-file-offer:hover {
    border-color: var(--accent);
  }
  .find-file-offer .q {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: ui-monospace, monospace;
    font-weight: 500;
  }
  .find-file-offer kbd {
    font-size: 10px;
    color: var(--muted);
  }
  .find-file h2 {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .find-file h2 code {
    font-size: 0.8em;
  }
  .matches {
    list-style: none;
    margin: 14px 0 0;
    padding: 0;
    border: 1px solid var(--line);
    border-radius: 10px;
    overflow: hidden;
  }
  .matches li + li {
    border-top: 1px solid var(--line);
  }
  .matches button {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 12px;
    border: 0;
    background: none;
    color: var(--fg);
    text-align: left;
    font: 13px ui-monospace, monospace;
    cursor: pointer;
  }
  .matches button:hover,
  .matches button:focus-visible {
    background: var(--soft);
    outline: none;
  }
  .path {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .dir {
    color: var(--muted);
  }
  .in-pr,
  .read-only {
    flex: none;
    font: 10px ui-monospace, monospace;
    border-radius: 30px;
    padding: 2px 8px;
    border: 1px solid var(--line);
  }
  .in-pr {
    background: var(--goodbg);
    color: var(--good);
    border-color: transparent;
  }
  .read-only {
    background: var(--code);
    color: var(--muted);
  }
  .note {
    background: var(--warnbg);
    color: var(--fg);
    padding: 8px 10px;
    border-radius: 8px;
  }
  .count {
    font-size: 12px;
  }
</style>
